import React from "react";
import ReactSelect, { type MultiValue, type Props as ReactSelectProps } from "react-select";
import { ChevronDown, X } from "lucide-react";
import { normalize } from "@/lib/utils";
import { selectStyles, type Option } from "./selectStyles";

export type { Option } from "./selectStyles";

interface MultiSelectProps extends Omit<ReactSelectProps<Option, true>, 'isMulti' | 'styles'> {
    value?: Option[];
    onChange?: (newValue: MultiValue<Option>) => void;
    options: Option[];
    placeholder?: string;
    closeMenuOnSelect?: boolean;
    multiSelect? : boolean
}

const SEPARATORS = /[,;\n\r\t]/;

/** Matching key tolerant to case, accents and extra spacing */
const matchKey = (s: string) => normalize(s).replace(/\s+/g, " ").trim();

export const MultiSelect: React.FC<MultiSelectProps> = (
    {
        value,
        onChange,
        options,
        placeholder,
        closeMenuOnSelect = false,
        multiSelect = true,
        ...props
    }) => {
    const optionByKey = React.useMemo(() => {
        const map = new Map<string, Option>();
        // Labels take precedence: a pasted list holds names, not ids
        for (const opt of options) {
            const valueKey = matchKey(opt.value);
            if (valueKey && !map.has(valueKey)) {
                map.set(valueKey, opt);
            }
        }
        for (const opt of options) {
            const labelKey = matchKey(opt.label);
            if (labelKey) {
                map.set(labelKey, opt);
            }
        }
        return map;
    }, [options]);

    /** Pasting "OMNI Jour, OMNI Soir" selects every matching option at once */
    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        if (multiSelect === false || !onChange) {
            return;
        }

        const text = event.clipboardData.getData("text");
        if (!SEPARATORS.test(text) || optionByKey.has(matchKey(text))) {
            return;
        }

        const names = text.split(SEPARATORS).map(name => name.trim()).filter(Boolean);
        if (names.length === 0) {
            return;
        }

        event.preventDefault();

        const selected = value ?? [];
        const selectedValues = new Set(selected.map(opt => opt.value));
        const added: Option[] = [];

        for (const name of names) {
            const option = optionByKey.get(matchKey(name));

            if (option && !selectedValues.has(option.value)) {
                selectedValues.add(option.value);
                added.push(option);
            }
        }

        if (added.length > 0) {
            onChange([...selected, ...added]);
        }
    };

    return (
        <div onPaste={handlePaste}>
            <ReactSelect<Option, true>
                isMulti={multiSelect !== false ? true : void 0}
                options={options}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                closeMenuOnSelect={closeMenuOnSelect}
                styles={selectStyles}
                components={{
                    DropdownIndicator: () => (
                        <div className="p-1.5 text-gray-400">
                            <ChevronDown className="w-4 h-4"/>
                        </div>
                    ),
                    ClearIndicator: (props) => (
                        <div {...props.innerProps} className="p-1.5 text-gray-400 hover:text-gray-600 cursor-pointer">
                            <X className="w-e h-4"/>
                        </div>
                    )
                }}
                {...props}
            />
        </div>
    );
};
